import { Badge } from "@car/ui/components/badge";
import { Button, buttonVariants } from "@car/ui/components/button";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@car/ui/components/empty";
import { Image } from "@car/ui/components/image";
import { NativeSelect, NativeSelectOption } from "@car/ui/components/native-select";
import { cn } from "@car/ui/lib/utils";
import { vehicleFilterFields } from "@car/api/catalog-config";
import { breadcrumbJsonLd } from "@car/api/seo";
import { CarAlertIcon } from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { z } from "zod";

import { MobileNavigation, SiteFooter } from "@/components/site-footer";
import { BannerStrip, SiteHeader } from "@/components/site-header";
import { VehicleCard, VehicleSkeleton } from "@/components/vehicle-card";
import {
  bodyKeys,
  bodyLabels,
  fuelKeys,
  fuelLabels,
  isBodyKey,
  isSortKey,
  loadSiteContent,
  plateKindLabels,
  plateKinds,
  useSiteContent,
  type FuelKey,
  type PlateKind,
  type SiteContent,
} from "@/lib/content";
import { faNumber } from "@/lib/format";
import { pageMeta, pageTitle, siteUrl } from "@/lib/seo";
import { orpc } from "@/utils/orpc";

import { publicPageHeaders } from "./-public-cache";

// the server's filter contract, with anything unreadable in the URL dropped instead of failing
const vehiclesSearchSchema = z.object({
  fuel: vehicleFilterFields.fuel.catch(undefined),
  body: vehicleFilterFields.body.catch(undefined),
  plate: vehicleFilterFields.plate.catch(undefined),
  brand: vehicleFilterFields.brand.catch(undefined),
  min: vehicleFilterFields.min.catch(undefined),
  max: vehicleFilterFields.max.catch(undefined),
  sort: vehicleFilterFields.sort.catch(undefined),
});

type VehiclesSearch = z.infer<typeof vehiclesSearchSchema>;

const noFilters = {
  fuel: undefined,
  body: undefined,
  plate: undefined,
  brand: undefined,
  min: undefined,
  max: undefined,
  sort: undefined,
} satisfies Record<keyof VehiclesSearch, undefined>;

const isPlateKind = (value: string): value is PlateKind => plateKinds.some((key) => key === value);

/** پله‌های بازه قیمت (تومان)؛ مقداری دیگر که در نشانی آمده باشد هم به فهرست اضافه می‌شود */
const priceSteps = [1, 1.5, 2, 2.5, 3, 4, 5, 7, 10].map((billion) => billion * 1_000_000_000);
const billionFormat = new Intl.NumberFormat("fa-IR", { maximumFractionDigits: 3 });
const priceStepLabel = (value: number) => `${billionFormat.format(value / 1_000_000_000)} میلیارد`;
const stepsWith = (current: number | undefined) =>
  current === undefined || priceSteps.includes(current)
    ? priceSteps
    : [...priceSteps, current].sort((a, b) => a - b);

export const Route = createFileRoute("/vehicles")({
  validateSearch: vehiclesSearchSchema,
  loaderDeps: ({ search }) => search,
  // site content: already cached by the root route; here for the title and description.
  // the list: fetched on the server too, so the HTML carries the cards (search engines,
  // link previews); a filter change ("stay") keeps showing the old list while it fetches
  loader: async ({ context, deps, cause }) => {
    if (cause !== "stay") {
      await context.queryClient
        .ensureQueryData(orpc.site.vehicles.list.queryOptions({ input: deps }))
        // the page fetches it again in the browser
        .catch(() => null);
    }
    return loadSiteContent(context.queryClient);
  },
  head: ({ loaderData }) => ({
    meta: [
      ...pageMeta(
        pageTitle(loaderData?.vehiclesPage.title ?? "همه خودروها"),
        loaderData?.vehiclesPage.metaDescription,
      ),
      {
        "script:ld+json": breadcrumbJsonLd(siteUrl, [
          { name: "خانه", path: "/" },
          { name: "خودروها", path: "/vehicles" },
        ]),
      },
    ],
  }),
  headers: publicPageHeaders,
  component: VehiclesPage,
});

type FuelCategory = { key: FuelKey; blurb: string; image: string };

/** کاشی‌های نوع سوخت؛ عکس و متن هر کاشی از مدیریت محتوا (بلوک vehiclesPage) */
const fuelCategories = (text: SiteContent["vehiclesPage"]): FuelCategory[] => [
  { key: "electric", blurb: text.electricBlurb, image: text.electricImage },
  { key: "hybrid", blurb: text.hybridBlurb, image: text.hybridImage },
  { key: "gasoline", blurb: text.gasolineBlurb, image: text.gasolineImage },
];

function VehiclesPage() {
  const { vehiclesPage: text } = useSiteContent();
  const search = Route.useSearch();
  const { fuel, body, plate, brand, min, max, sort } = search;
  const navigate = Route.useNavigate();
  // the filter is the URL; the server applies it and counts the whole catalog
  const query = useQuery({
    ...orpc.site.vehicles.list.queryOptions({ input: search }),
    placeholderData: (previous) => previous,
  });
  const loadedBrands = query.data?.brands ?? [];
  // a brand from a shared link stays selectable even when it is no longer on sale
  const brands = brand && !loadedBrands.includes(brand) ? [...loadedBrands, brand] : loadedBrands;
  const banners = useQuery(orpc.site.home.queryOptions());
  const filtered = query.data?.vehicles ?? [];
  const total = query.data?.total ?? 0;
  const counts = query.data?.counts;
  // a fuel type with no cars is hidden, unless a shared link already filters by it
  const hasCars = (key: FuelKey) => !counts || counts[key] > 0 || fuel === key;
  const categories = fuelCategories(text).filter((category) => hasCars(category.key));

  const setSearch = (patch: Partial<VehiclesSearch>) =>
    navigate({
      search: (prev) => ({ ...prev, ...patch }),
      replace: true,
      resetScroll: false,
    });

  const hasFilters = Object.values(search).some((value) => value !== undefined);

  return (
    <main className="min-h-[100dvh] bg-background pb-20 text-foreground md:pb-0">
      <SiteHeader />

      <section className="border-b bg-muted/45">
        <div className="mx-auto max-w-[1400px] px-4 py-10 sm:px-6 sm:py-14">
          <nav aria-label="مسیر صفحه" className="text-sm text-muted-foreground">
            <a href="/" className="hover:text-foreground">
              خانه
            </a>
            <span aria-hidden="true" className="mx-2">
              /
            </span>
            <span className="font-bold text-foreground">خودروها</span>
          </nav>
          <h1 className="mt-4 text-3xl font-black sm:text-5xl">
            {fuel ? `خودروهای ${fuelLabels[fuel]}` : text.title}
          </h1>
          <p className="mt-3 max-w-[55ch] leading-7 text-muted-foreground">{text.lede}</p>

          <div className="mt-8">
            <h2 className="font-black">{text.fuelHeading}</h2>
            <div
              className={cn(
                "mt-4 grid gap-4 sm:grid-cols-2",
                categories.length === 3 && "lg:grid-cols-[1.3fr_1fr_1fr]",
              )}
            >
              {categories.map((category, index) => (
                <FuelCategoryTile
                  key={category.key}
                  category={category}
                  count={counts?.[category.key] ?? 0}
                  active={fuel === category.key}
                  featured={categories.length === 3 && index === 0}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-10 sm:py-14">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6">
          <div className="mb-6">
            <BannerStrip
              banners={(banners.data?.banners ?? []).filter(
                (banner) => banner.position === "vehicles_page",
              )}
            />
          </div>
          <div className="z-30 rounded-2xl border bg-background/95 p-3 shadow-sm backdrop-blur-xl sm:sticky sm:top-[4.5rem] lg:top-20">
            <div className="flex flex-wrap items-center gap-2">
              <div role="group" aria-label="فیلتر نوع سوخت" className="flex flex-wrap gap-1.5">
                <Button
                  variant={fuel ? "outline" : "default"}
                  className="rounded-full"
                  aria-pressed={!fuel}
                  onClick={() => setSearch({ fuel: undefined })}
                >
                  همه
                </Button>
                {fuelKeys.filter(hasCars).map((key) => (
                  <Button
                    key={key}
                    variant={fuel === key ? "default" : "outline"}
                    className="rounded-full"
                    aria-pressed={fuel === key}
                    onClick={() => setSearch({ fuel: fuel === key ? undefined : key })}
                  >
                    {fuelLabels[key]}
                  </Button>
                ))}
              </div>
              <div className="ms-auto flex flex-wrap items-center gap-2">
                <NativeSelect
                  aria-label="فیلتر نوع پلاک"
                  value={plate ?? "all"}
                  onChange={(event) =>
                    setSearch({
                      plate: isPlateKind(event.target.value) ? event.target.value : undefined,
                    })
                  }
                >
                  <NativeSelectOption value="all">همه پلاک‌ها</NativeSelectOption>
                  {plateKinds.map((key) => (
                    <NativeSelectOption key={key} value={key}>
                      {plateKindLabels[key]}
                    </NativeSelectOption>
                  ))}
                </NativeSelect>
                <NativeSelect
                  aria-label="فیلتر برند"
                  value={brand ?? ""}
                  onChange={(event) => setSearch({ brand: event.target.value || undefined })}
                >
                  <NativeSelectOption value="">همه برندها</NativeSelectOption>
                  {brands.map((name) => (
                    <NativeSelectOption key={name} value={name}>
                      {name}
                    </NativeSelectOption>
                  ))}
                </NativeSelect>
                {/* بازه قیمت به قیمت همان پلاک انتخاب‌شده (وگرنه قیمت اصلی هر خودرو) */}
                <NativeSelect
                  aria-label={plate ? `حداقل قیمت ${plateKindLabels[plate]}` : "حداقل قیمت"}
                  value={min ?? ""}
                  onChange={(event) =>
                    setSearch({ min: event.target.value ? Number(event.target.value) : undefined })
                  }
                >
                  <NativeSelectOption value="">از هر قیمتی</NativeSelectOption>
                  {stepsWith(min)
                    .filter((step) => max === undefined || step <= max)
                    .map((step) => (
                      <NativeSelectOption key={step} value={step}>
                        از {priceStepLabel(step)}
                      </NativeSelectOption>
                    ))}
                </NativeSelect>
                <NativeSelect
                  aria-label={plate ? `حداکثر قیمت ${plateKindLabels[plate]}` : "حداکثر قیمت"}
                  value={max ?? ""}
                  onChange={(event) =>
                    setSearch({ max: event.target.value ? Number(event.target.value) : undefined })
                  }
                >
                  <NativeSelectOption value="">تا هر قیمتی</NativeSelectOption>
                  {stepsWith(max)
                    .filter((step) => min === undefined || step >= min)
                    .map((step) => (
                      <NativeSelectOption key={step} value={step}>
                        تا {priceStepLabel(step)}
                      </NativeSelectOption>
                    ))}
                </NativeSelect>
                <NativeSelect
                  aria-label="فیلتر نوع بدنه"
                  value={body ?? "all"}
                  onChange={(event) =>
                    setSearch({
                      body: isBodyKey(event.target.value) ? event.target.value : undefined,
                    })
                  }
                >
                  <NativeSelectOption value="all">همه بدنه‌ها</NativeSelectOption>
                  {bodyKeys.map((key) => (
                    <NativeSelectOption key={key} value={key}>
                      {bodyLabels[key]}
                    </NativeSelectOption>
                  ))}
                </NativeSelect>
                <NativeSelect
                  aria-label="مرتب‌سازی"
                  value={sort ?? "default"}
                  onChange={(event) =>
                    setSearch({
                      sort: isSortKey(event.target.value) ? event.target.value : undefined,
                    })
                  }
                >
                  <NativeSelectOption value="default">مرتب‌سازی پیش‌فرض</NativeSelectOption>
                  <NativeSelectOption value="cheapest">ارزان‌ترین</NativeSelectOption>
                  <NativeSelectOption value="expensive">گران‌ترین</NativeSelectOption>
                </NativeSelect>
              </div>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
            <p aria-live="polite" className="text-sm text-muted-foreground">
              {query.isPending
                ? "در حال بارگذاری خودروها"
                : `نمایش ${faNumber(filtered.length)} خودرو از ${faNumber(total)} خودرو`}
            </p>
            {hasFilters && filtered.length > 0 && (
              <Button variant="ghost" className="text-primary" onClick={() => setSearch(noFilters)}>
                حذف همه فیلترها
              </Button>
            )}
          </div>

          {query.isPending ? (
            <div className="mt-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {Array.from({ length: 6 }).map((_, index) => (
                <VehicleSkeleton key={index} />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <Empty className="mt-4 border bg-card">
              <EmptyHeader>
                <EmptyMedia variant="icon">
                  <HugeiconsIcon icon={CarAlertIcon} strokeWidth={1.6} />
                </EmptyMedia>
                <EmptyTitle>خودرویی با این فیلترها موجود نیست</EmptyTitle>
                <EmptyDescription>
                  ترکیب دیگری را امتحان کنید یا همه فیلترها را حذف کنید.
                </EmptyDescription>
              </EmptyHeader>
              <EmptyContent>
                <Button onClick={() => setSearch(noFilters)}>حذف همه فیلترها</Button>
              </EmptyContent>
            </Empty>
          ) : (
            <div className="mt-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {filtered.map((vehicle) => (
                <VehicleCard key={vehicle.id} vehicle={vehicle} />
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="border-y bg-primary py-14 text-primary-foreground sm:py-16">
        <div className="mx-auto flex max-w-[1400px] flex-col items-start justify-between gap-6 px-4 sm:flex-row sm:items-center sm:px-6">
          <div>
            <h2 className="text-2xl font-black sm:text-3xl">{text.financeTitle}</h2>
            <p className="mt-2 text-primary-foreground/75">{text.financeText}</p>
          </div>
          <a
            href="/guide"
            className={cn(buttonVariants({ variant: "secondary", size: "lg" }), "shrink-0")}
          >
            {text.financeCta}
          </a>
        </div>
      </section>

      <SiteFooter />
      <MobileNavigation />
    </main>
  );
}

function FuelCategoryTile({
  category,
  count,
  active,
  featured,
}: {
  category: FuelCategory;
  count: number;
  active: boolean;
  featured: boolean;
}) {
  return (
    <Link
      from={Route.fullPath}
      search={(prev) => ({
        ...prev,
        fuel: prev.fuel === category.key ? undefined : category.key,
      })}
      resetScroll={false}
      aria-pressed={active}
      className={cn(
        "group relative isolate flex min-h-52 flex-col justify-end overflow-hidden rounded-2xl border p-5 transition-shadow focus-visible:outline-3 focus-visible:outline-ring",
        featured && "sm:col-span-2 lg:col-span-1 lg:min-h-60",
        active && "ring-2 ring-ring",
      )}
    >
      <Image
        src={category.image}
        alt=""
        fill
        sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 460px"
        className="-z-10 object-cover transition-transform duration-500 group-hover:scale-[1.04]"
      />
      <span
        aria-hidden="true"
        className="absolute inset-0 -z-10 bg-gradient-to-t from-brand-charcoal/90 via-brand-charcoal/35 to-brand-charcoal/10"
      />
      {active && (
        <Badge className="absolute top-4 start-4 bg-primary text-primary-foreground">
          فیلتر فعال
        </Badge>
      )}
      <p className="text-sm font-bold text-white/80">{faNumber(count)} خودرو</p>
      <h3 className="mt-1 text-2xl font-black text-white">{fuelLabels[category.key]}</h3>
      <p className="mt-2 max-w-[34ch] text-sm leading-6 text-white/75">{category.blurb}</p>
    </Link>
  );
}
